import { cardMask } from './card';
import {
  type MembershipPaymentFault,
  type MembershipUpstream,
  paymentFaultOf,
} from './errors';
import type { MembershipFormatters } from './format';
import { isRecord } from './guards';
import type { MembershipLine } from './lines';
import { isTrialEligible, trialEndOf, trialKey } from './trial';
import type { Membership, MembershipCard, MembershipPlan } from './types';

/** Path of the membership page. */
export const MEMBERSHIP_PATH = '/cabinet/membership';

/** Status reported by `POST /memberships` and `POST /memberships/me/confirm-card`. */
export type MembershipPurchaseStatus =
  | 'trialing'
  | 'active'
  | 'action_required'
  | 'pending';

/** An Adyen action relayed by the backend; handed to the card form untouched. */
export interface MembershipPaymentAction {
  /** `threeDS2` for an in-page 3DS, `redirect` for a bank page. */
  type: string;
  [key: string]: unknown;
}

/** Answer of a purchase or of a card confirmation. */
export interface MembershipPurchaseResult {
  status: MembershipPurchaseStatus;
  /** Present only with `action_required`. */
  action: MembershipPaymentAction | null;
  membership: Membership | null;
}

/** Body of `POST /memberships`; the payment fields come from the card form and are sent as is. */
export interface MembershipPurchaseInput {
  planId: string;
  /** Courier zone the plan was offered in. */
  zoneId: string;
  paymentMethod: Record<string, unknown>;
  browserInfo: Record<string, unknown> | null;
  origin: string;
  /** Where the bank sends the customer back after a redirect 3DS. */
  returnUrl: string;
}

/** One-time 3DS result (`threeDSResult` from the form, `redirectResult` from the bank); never sent twice. */
export type MembershipCardDetails = Record<string, unknown>;

/**
 * Why a purchase request failed, as far as the storefront can tell.
 * `unknown_outcome` — no answer, a server fault or a 503 — means the purchase
 * may have gone through and must be checked through `/me`, never resent;
 * `payment_rejected` — `MEMBERSHIP_PAYMENT_REJECTED` — means the payment service refused it and
 * nothing was charged.
 */
export type MembershipPurchaseErrorKind =
  | 'plan_not_found'
  | 'already_exists'
  | 'card_refused'
  | 'payment_rejected'
  | 'unauthorized'
  | 'rejected'
  | 'unknown_outcome';

const KIND_BY_CODE: Record<string, MembershipPurchaseErrorKind> = {
  MEMBERSHIP_PLAN_NOT_FOUND: 'plan_not_found',
  MEMBERSHIP_ALREADY_EXISTS: 'already_exists',
  MEMBERSHIP_CARD_REFUSED: 'card_refused',
};

const KIND_BY_STATUS: Record<number, MembershipPurchaseErrorKind> = {
  401: 'unauthorized',
  404: 'plan_not_found',
  409: 'already_exists',
  422: 'card_refused',
};

const KIND_BY_PAYMENT_FAULT: Record<
  MembershipPaymentFault,
  MembershipPurchaseErrorKind
> = {
  unknown: 'unknown_outcome',
  rejected: 'payment_rejected',
};

/**
 * Classifies a failed purchase request. A payment service fault (`paymentFaultOf`)
 * comes first, then the backend code, then the HTTP status.
 */
export const purchaseErrorKind = (
  status: number | undefined,
  code: string | undefined,
): MembershipPurchaseErrorKind => {
  const fault = paymentFaultOf(status, code);
  if (fault) {
    return KIND_BY_PAYMENT_FAULT[fault];
  }
  const byCode =
    code !== undefined && Object.hasOwn(KIND_BY_CODE, code)
      ? KIND_BY_CODE[code]
      : undefined;
  if (byCode) {
    return byCode;
  }
  if (status === undefined) {
    return 'unknown_outcome';
  }
  const byStatus = KIND_BY_STATUS[status];
  if (byStatus) {
    return byStatus;
  }
  return status >= 500 ? 'unknown_outcome' : 'rejected';
};

/**
 * A failed purchase request. Carries only its classification and what the
 * backend said about the payment service, never the request or the response,
 * so no payment field can reach a report through it.
 */
export class MembershipPurchaseError extends Error {
  readonly kind: MembershipPurchaseErrorKind;
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly upstream: MembershipUpstream | undefined;

  constructor(
    kind: MembershipPurchaseErrorKind,
    status?: number,
    code?: string,
    upstream?: MembershipUpstream,
  ) {
    super(`Membership purchase failed: ${kind}`);
    this.name = 'MembershipPurchaseError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.upstream = upstream;
  }
}

/** Anything a purchase request threw, as a `MembershipPurchaseError`; the unknown is an unknown outcome. */
export const asPurchaseError = (error: unknown): MembershipPurchaseError =>
  error instanceof MembershipPurchaseError
    ? error
    : new MembershipPurchaseError('unknown_outcome');

/**
 * What the card form does after a submit or 3DS callback: show the action,
 * stop (the store took over), fail, or ignore a call that came at the wrong
 * time (a second submit while one is in flight).
 */
export type CardFormReply =
  | { kind: 'action'; action: MembershipPaymentAction }
  | { kind: 'done' }
  | { kind: 'failed' }
  | { kind: 'ignored' };

/** The form shows the action of `action_required`; every other status ends its part. */
export const toCardFormReply = (
  result: MembershipPurchaseResult,
): CardFormReply =>
  result.status === 'action_required' && result.action
    ? { kind: 'action', action: result.action }
    : { kind: 'done' };

/** The part of a plan a purchase needs once the plan list is gone (after a bank redirect). */
export type MembershipPlanRef = Pick<
  MembershipPlan,
  'id' | 'code' | 'priceMinor' | 'currency'
>;

export const toPlanRef = ({
  id,
  code,
  priceMinor,
  currency,
}: MembershipPlanRef): MembershipPlanRef => ({
  id,
  code,
  priceMinor,
  currency,
});

/** What the welcome banner states; taken from the answer because trial eligibility is unknown beforehand. */
export interface MembershipWelcome {
  status: 'trialing' | 'active';
  trialEnd: string | null;
  /** Price of the next charge, in minor units. */
  priceMinor: number;
  currency: string;
}

/** Welcome of a settled purchase; the plan fills in when the answer carries no membership. */
export const toWelcome = (
  status: 'trialing' | 'active',
  membership: Membership | null,
  plan: MembershipPlanRef,
): MembershipWelcome => ({
  status,
  trialEnd: membership?.trialEnd ?? null,
  priceMinor: membership?.priceMinor ?? plan.priceMinor,
  currency: membership?.plan.currency ?? plan.currency,
});

/** Closable banner of the membership page after a purchase or a member card request. */
export type MembershipNotice =
  | { kind: 'welcome'; welcome: MembershipWelcome }
  | { kind: 'declined' }
  /** `benefitsUntil` is `null` when the answer says the benefits are already off. */
  | { kind: 'cancelled'; benefitsUntil: string | null }
  | { kind: 'upgraded'; planName: string }
  | { kind: 'planKept'; planName: string }
  | { kind: 'planUnavailable' }
  /**
   * `restored`: the card was replaced while overdue and the benefits are back.
   * `card`: the new card as the answer names it; the banner leaves it out when unknown.
   */
  | { kind: 'cardUpdated'; restored: boolean; card?: MembershipCard | null };

/** Saved before a redirect 3DS and read back when the bank returns. */
export interface MembershipRedirectContext {
  plan: MembershipPlanRef;
  zoneId: string | null;
  /** Page the purchase started on (`clubOrigin`); absent in a marker written before it existed. */
  origin?: string;
}

/** A purchase answered `pending` and is being activated. */
export interface MembershipActivation {
  planId: string;
  planCode: string;
}

/**
 * Why Join could not finish a purchase. `payment_unavailable`: the outcome
 * was unknown and `/me` showed no membership; `payment_rejected`: the payment
 * service refused the request and nothing was charged.
 */
export type MembershipPurchaseFailure =
  | 'card_refused'
  | 'payment_unavailable'
  | 'payment_rejected'
  | 'not_confirmed'
  | 'form_unavailable'
  | 'plan_not_found'
  | 'already_member';

/** Payload of the membership funnel events that only name the plan. */
export interface MembershipPlanEventPayload {
  planCode: string;
}

/** Payload of `Membership: Buy Clicked`. */
export interface MembershipBuyClickedPayload
  extends MembershipPlanEventPayload {
  /** The purchase starts with a free trial for this customer (`trialEligible` of the plan in `/plans`). */
  hasTrial: boolean;
}

/** Payload of `Membership: Purchase Succeeded`. */
export interface MembershipPurchaseSucceededPayload
  extends MembershipPlanEventPayload {
  status: 'trialing' | 'active' | 'pending';
  /** The purchase started a free trial; `null` for `pending`, where it is not known yet. */
  trial: boolean | null;
}

/** Reason of `Membership: Purchase Failed`; `charge_failed` is a pending first charge that did not go through. */
export type MembershipPurchaseFailureReason =
  | MembershipPurchaseFailure
  | 'charge_failed';

/** Payload of `Membership: Purchase Failed`. */
export interface MembershipPurchaseFailedPayload
  extends MembershipPlanEventPayload {
  reason: MembershipPurchaseFailureReason;
}

/** Funnel events the purchase store emits; mapped to analytics by the composition root. */
export type MembershipPurchaseEvent =
  | { name: 'cardSubmitted'; payload: MembershipPlanEventPayload }
  | { name: 'threeDSShown'; payload: MembershipPlanEventPayload }
  | { name: 'purchaseSucceeded'; payload: MembershipPurchaseSucceededPayload }
  | { name: 'purchaseFailed'; payload: MembershipPurchaseFailedPayload };

/**
 * An error of the card form reduced to its name, message and stack: an SDK
 * error may carry the 3DS action or the payment data in `cause` or in own fields.
 */
export interface MembershipFormErrorSummary {
  name: string;
  message: string;
  stack?: string;
}

/** Name, message and stack of anything the card form threw; nothing else of it is kept. */
export const summarizeFormError = (
  error: unknown,
): MembershipFormErrorSummary =>
  error instanceof Error
    ? { name: error.name, message: error.message, stack: error.stack }
    : {
        name: 'UnknownError',
        message: typeof error === 'string' ? error : typeof error,
      };

/**
 * Something worth a Sentry event. No variant carries a payment field: an SDK
 * error is reduced to its name and message, and a lost chunk is an import error.
 */
export type MembershipPurchaseIncident =
  | {
      reason: 'purchaseFailed';
      kind: MembershipPurchaseErrorKind;
      status?: number;
      code?: string;
      upstream?: MembershipUpstream;
      level: 'warning' | 'error';
    }
  | { reason: 'cardRefused'; status?: number; code?: string }
  | {
      reason: 'confirmFailed';
      kind: MembershipPurchaseErrorKind;
      status?: number;
      code?: string;
      upstream?: MembershipUpstream;
    }
  | {
      reason: 'adyenInitFailed';
      stage: 'init' | 'runtime';
      error: MembershipFormErrorSummary;
    }
  /** The card form's code never arrived; routine after a deploy, not an SDK fault. */
  | { reason: 'cardFormChunkFailed'; error: unknown };

/** Incidents of the card form itself, shared by Join (`?modal=club-join`) and the "Add card" sheet (`?modal=club-card`). */
export type MembershipFormIncident = Extract<
  MembershipPurchaseIncident,
  { reason: 'adyenInitFailed' | 'cardFormChunkFailed' }
>;

/**
 * What the card sheet says about today's charge: nothing is charged for a
 * plan with a trial for this customer (`trialEligible` from `/plans`, the
 * card is only verified), the price is charged today otherwise.
 */
export const checkoutPaymentLine = (
  plan: MembershipPlan,
  f: MembershipFormatters,
): MembershipLine =>
  isTrialEligible(plan)
    ? { key: 'membership:checkoutNotChargedToday' }
    : {
        key: 'membership:checkoutChargedToday',
        params: { price: f.money(plan.priceMinor, plan.currency) },
      };

/**
 * A trial of up to this many days gets no "we'll remind you 3 days before"
 * promise: the backend (`MEMBERSHIP_TRIAL_ENDING_NOTICE_DAYS`) then sends the
 * reminder right away.
 */
const TRIAL_REMINDER_DAYS = 3;

/**
 * Renewal consent under the purchase button, worded as in the backend design:
 * the trial, its end — `now` plus the trial, the same calendar arithmetic the
 * backend uses — and the price after it for a plan with a trial for this
 * customer, without the reminder promise for a trial of up to 3 days; the
 * monthly renewal otherwise. `null` when the trial end cannot be dated: the
 * consent checkbox above the button already states the renewal.
 */
export const checkoutConsentLine = (
  plan: MembershipPlan,
  f: MembershipFormatters,
  now: Date,
): MembershipLine | null => {
  const price = f.money(plan.priceMinor, plan.currency);
  if (!isTrialEligible(plan)) {
    return { key: 'membership:checkoutConsentNoTrial', params: { price } };
  }
  const end = trialEndOf(now, plan.trialLength, plan.trialUnit);
  const date = Number.isNaN(end.getTime()) ? null : f.date(end.toISOString());
  if (!date) {
    return null;
  }
  const isShort =
    plan.trialUnit === 'day' && plan.trialLength <= TRIAL_REMINDER_DAYS;
  return {
    key: isShort
      ? 'membership:checkoutConsentTrialShort'
      : trialKey('membership:checkoutConsentTrial', plan.trialUnit),
    params: { count: plan.trialLength, price, date },
  };
};

const FAILURE_KEYS: Partial<Record<MembershipPurchaseFailure, string>> = {
  card_refused: 'membership:purchaseDeclined',
  payment_unavailable: 'membership:purchaseUnavailable',
  payment_rejected: 'membership:paymentNothingCharged',
  form_unavailable: 'membership:purchaseUnavailable',
  not_confirmed: 'membership:purchaseNotConfirmed',
};

/** Message above the card form; `null` for failures that replace the form with an explanation. */
export const purchaseFailureLine = (
  failure: MembershipPurchaseFailure,
): MembershipLine | null => {
  const key = FAILURE_KEYS[failure];
  return key ? { key, tone: 'warning' } : null;
};

/** i18n keys of a welcome line: today's charge, trial end, or neither. */
export interface WelcomeLineKeys {
  charged: string;
  trial: string;
  generic: string;
}

/**
 * The one rule of a welcome line, stated by the purchase answer: an `active`
 * purchase was charged today, a trial names its end, anything else is
 * generic. The club page banner and Welcome of Join differ only in the keys.
 */
export const welcomeLineOf = (
  welcome: MembershipWelcome,
  f: MembershipFormatters,
  keys: WelcomeLineKeys,
): MembershipLine => {
  if (welcome.status === 'active') {
    return {
      key: keys.charged,
      params: { price: f.money(welcome.priceMinor, welcome.currency) },
    };
  }
  const date = f.date(welcome.trialEnd);
  return date ? { key: keys.trial, params: { date } } : { key: keys.generic };
};

/** Text of the membership page banner. */
export const noticeLine = (
  notice: MembershipNotice,
  f: MembershipFormatters,
): MembershipLine => {
  switch (notice.kind) {
    case 'welcome':
      return welcomeLineOf(notice.welcome, f, {
        charged: 'membership:welcomeCharged',
        trial: 'membership:welcomeTrial',
        generic: 'membership:welcome',
      });
    case 'declined':
      return { key: 'membership:purchaseDeclined', tone: 'warning' };
    case 'cancelled': {
      const date = f.date(notice.benefitsUntil);
      return date
        ? { key: 'membership:noticeCancelledUntil', params: { date } }
        : { key: 'membership:noticeCancelled' };
    }
    case 'upgraded':
      return {
        key: 'membership:noticeUpgraded',
        params: { planName: notice.planName },
      };
    case 'planKept':
      return {
        key: 'membership:noticePlanKept',
        params: { planName: notice.planName },
      };
    case 'planUnavailable':
      return { key: 'membership:noticePlanUnavailable', tone: 'warning' };
    case 'cardUpdated': {
      const card = cardMask(notice.card);
      if (card) {
        return {
          key: notice.restored
            ? 'membership:noticeCardRestoredTo'
            : 'membership:noticeCardUpdatedTo',
          params: { card },
        };
      }
      return {
        key: notice.restored
          ? 'membership:noticeCardRestored'
          : 'membership:noticeCardUpdated',
      };
    }
  }
};

/** Lifetime of a session marker: the backend expires an abandoned purchase after 30 minutes. */
export const MEMBERSHIP_MARKER_TTL_MS = 30 * 60 * 1000;

/** Clock step back tolerated between writing and reading a marker. */
const MARKER_CLOCK_SKEW_MS = 60 * 1000;

/** Serialises a marker stamped with the customer and the time. */
export const encodeMarker = (
  value: unknown,
  customerId: string,
  now: number,
): string => JSON.stringify({ customerId, savedAt: now, value });

/**
 * Reads a marker back: `null` when it is absent or unreadable, written for
 * another customer (or nobody is signed in), or older than 30 minutes.
 */
export const decodeMarker = (
  raw: string | null,
  customerId: string | null,
  now: number,
): unknown => {
  if (raw === null || customerId === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    !isRecord(parsed) ||
    parsed.customerId !== customerId ||
    typeof parsed.savedAt !== 'number'
  ) {
    return null;
  }
  const age = now - parsed.savedAt;
  if (age > MEMBERSHIP_MARKER_TTL_MS || age < -MARKER_CLOCK_SKEW_MS) {
    return null;
  }
  return parsed.value ?? null;
};

const isPlanRef = (value: unknown): value is MembershipPlanRef =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.code === 'string' &&
  typeof value.priceMinor === 'number' &&
  typeof value.currency === 'string';

export const isRedirectContext = (
  value: unknown,
): value is MembershipRedirectContext =>
  isRecord(value) &&
  isPlanRef(value.plan) &&
  (value.zoneId === null || typeof value.zoneId === 'string') &&
  (value.origin === undefined || typeof value.origin === 'string');

export const isActivation = (value: unknown): value is MembershipActivation =>
  isRecord(value) &&
  typeof value.planId === 'string' &&
  typeof value.planCode === 'string';

export const isWelcome = (value: unknown): value is MembershipWelcome =>
  isRecord(value) &&
  (value.status === 'trialing' || value.status === 'active') &&
  (value.trialEnd === null || typeof value.trialEnd === 'string') &&
  typeof value.priceMinor === 'number' &&
  typeof value.currency === 'string';
